import { SnapshotsMap } from 'app/agriculture/queries/snapshots/types/SnapshotsMap';
import {
    SnapshotInterface,
    SnapshotType,
} from 'app/agriculture/queries/snapshots/config/snapshotConfig';
import { Snapshot } from 'app/agriculture/queries/snapshots/types/Snapshot';
import { compareDesc } from 'date-fns';

/**
 * merges two maps so that the result map has all values from both maps
 * @param map1
 * @param map2
 */
function mergeMaps<T>(map1: Map<string, T[]>, map2: Map<string, T[]>): Map<string, T[]> {
    const mergedMap = new Map(map1);

    for (const [key, items] of map2.entries()) {
        const existing = mergedMap.get(key) ?? [];
        mergedMap.set(key, [...existing, ...items]);
    }

    return mergedMap;
}

/**
 * removes any duplicate items from an array
 * duplicates are determined by matching id properties
 * @param items
 */
function removeDuplicates<T extends { id: string }>(items: T[]): T[] {
    const map = items.reduce((map, item) => map.set(item.id, item), new Map<string, T>());
    return Array.from(map.values());
}

/**
 * applies the sort method to each set of arrays in a map
 * also removes duplicates
 * @param map
 * @param sort
 */
function sortMap<T extends { id: string }>(
    map: Map<string, T[]>,
    sort: ((a: T, b: T) => number) | undefined,
): Map<string, T[]> {
    const sortedMap = new Map(map);

    for (const [key, items] of map.entries()) {
        sortedMap.set(key, removeDuplicates(items.sort(sort)));
    }

    return sortedMap;
}

/**
 * combines an array of snapshot query results into a single map of snapshots by parent id
 */
export function combineSnapshotQueryData<
    T extends SnapshotType,
    I extends Snapshot = SnapshotInterface<T>,
>(results: { data: SnapshotsMap<I> }[]) {
    const mergedMap = results.reduce(
        (map, { data }) => mergeMaps(map, data),
        new Map<I['parentId'], I[]>(),
    );

    return sortMap(mergedMap, (a, b) => compareDesc(a.timestamp, b.timestamp));
}
